import { useCallback, useEffect, useRef, useState } from "react";
import { CheckSquare, Film, FolderInput, FolderOpen, Loader2, Pencil, Square, Star, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";

export default function AdminGallery({ token }) {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState(["Recent Events", "Historical"]);
  const [gallery, setGallery] = useState("Recent Events");
  const [newFolder, setNewFolder] = useState("");
  const [caption, setCaption] = useState("");
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);

  const readAllEntries = (reader) =>
    new Promise((resolve) => {
      const all = [];
      const step = () =>
        reader.readEntries((entries) => {
          if (!entries.length) return resolve(all);
          all.push(...entries);
          step();
        });
      step();
    });

  const walkEntry = async (entry, out) => {
    if (entry.isFile) {
      await new Promise((resolve) => entry.file((f) => { out.push(f); resolve(); }));
    } else if (entry.isDirectory) {
      const entries = await readAllEntries(entry.createReader());
      for (const e of entries) await walkEntry(e, out);
    }
  };

  const collectDroppedFiles = async (dataTransfer) => {
    const entries = Array.from(dataTransfer.items || [])
      .map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null))
      .filter(Boolean);
    if (!entries.length) return Array.from(dataTransfer.files || []);
    const out = [];
    for (const e of entries) await walkEntry(e, out);
    return out;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = await collectDroppedFiles(e.dataTransfer);
    const media = dropped.filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
    if (!media.length) {
      toast.error("No photos or videos found in what you dropped");
      return;
    }
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}-${f.size}`));
      return [...prev, ...media.filter((f) => !seen.has(`${f.name}-${f.size}`))];
    });
    toast.success(`${media.length} file${media.length === 1 ? "" : "s"} ready to upload`);
  };
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gallery/items", { headers });
      setItems(data);
      const f = await api.get("/gallery/folders");
      if (Array.isArray(f.data) && f.data.length) setFolders(f.data);
    } catch {
      toast.error("Failed to load gallery items");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!files.length) {
      toast.error("Choose photos or videos first");
      return;
    }
    const folder = gallery === "__new__" ? newFolder.trim() : gallery;
    if (!folder) {
      toast.error("Name the new folder");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("gallery", folder);
      fd.append("caption", caption);
      const { data } = await api.post("/admin/gallery/upload", fd, { headers, timeout: 300000 });
      toast.success(`Uploaded ${data.uploaded} item${data.uploaded === 1 ? "" : "s"} to "${folder}"`);
      setFiles([]);
      setCaption("");
      setNewFolder("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`, { headers });
      toast.success("Removed from gallery");
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState("");
  const [captionEditId, setCaptionEditId] = useState(null);
  const [captionValue, setCaptionValue] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkFolder, setBulkFolder] = useState("");
  const [bulkMoving, setBulkMoving] = useState(false);

  const toggleSelect = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const bulkMove = async () => {
    if (!bulkFolder || selected.length === 0) return;
    setBulkMoving(true);
    try {
      const { data } = await api.post("/admin/gallery/bulk-move", { ids: selected, gallery: bulkFolder }, { headers });
      setItems((prev) => prev.map((i) => (selected.includes(i.id) ? { ...i, gallery: data.folder } : i)));
      toast.success(`Moved ${data.moved} item${data.moved === 1 ? "" : "s"} to "${data.folder}"`);
      setSelected([]);
      setBulkFolder("");
      setSelectMode(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBulkMoving(false);
    }
  };

  const saveCaption = async (id) => {
    try {
      await api.put(`/admin/gallery/${id}`, { caption: captionValue }, { headers });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, caption: captionValue.trim() } : i)));
      setCaptionEditId(null);
      toast.success("Caption updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const moveItem = async (item, folder) => {
    if (!folder || folder === item.gallery) return;
    try {
      await api.put(`/admin/gallery/${item.id}`, { gallery: folder }, { headers });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, gallery: folder } : i)));
      toast.success(`Moved to "${folder}"`);
    } catch {
      toast.error("Couldn't move item");
    }
  };

  const setCover = async (id) => {    try {
      const { data } = await api.put(`/admin/gallery/${id}/cover`, {}, { headers });
      setItems((prev) => prev.map((i) => ({ ...i, is_cover: i.id === id })));
      toast.success(`Cover set for "${data.folder}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const folderCounts = items.reduce((acc, i) => {
    acc[i.gallery] = (acc[i.gallery] || 0) + 1;
    return acc;
  }, {});

  const renameFolder = async (oldLabel) => {
    const nl = renameValue.trim();
    setRenameTarget(null);
    if (!nl || nl === oldLabel) return;
    try {
      const { data } = await api.put("/admin/gallery/folders", { old_label: oldLabel, new_label: nl }, { headers });
      toast.success(`Folder renamed to "${data.label}"`);
      if (gallery === oldLabel) setGallery(data.label);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteFolder = async (label) => {
    try {
      const params = { label, mode: moveTarget ? "move" : "delete" };
      if (moveTarget) params.move_to = moveTarget;
      await api.delete("/admin/gallery/folders", { headers, params });
      toast.success(moveTarget ? `Items moved to "${moveTarget}"` : `Folder "${label}" and its ${folderCounts[label] || 0} items deleted`);
      setDeleteTarget(null);
      setMoveTarget("");
      if (gallery === label) setGallery(moveTarget || "Recent Events");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const inputCls =
    "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text transition-colors duration-300";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-12">
      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">Upload to Gallery</h2>
        <form
          data-testid="gallery-upload-form"
          onSubmit={upload}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`border bg-clay-card p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4 transition-all duration-300 ${
            dragging ? "border-clay-orange clay-glow border-dashed" : "border-clay-border"
          }`}
        >
          <div className={`sm:col-span-2 border border-dashed px-4 py-6 text-center transition-colors duration-300 ${
            dragging ? "border-clay-orange bg-clay-orange/10" : "border-clay-border/70"
          }`}>
            <Upload className={`w-6 h-6 mx-auto mb-2 ${dragging ? "text-clay-gold" : "text-clay-muted"}`} />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted">
              {dragging ? "Drop to add files" : "Drag a folder or photos/videos here — or use the picker below"}
            </p>
          </div>
          <div className="sm:col-span-2">
            <input
              data-testid="gallery-upload-file"
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="w-full text-sm text-clay-muted file:mr-4 file:py-3 file:px-6 file:border-0 file:bg-clay-elevated file:text-clay-text file:font-display file:uppercase file:tracking-wider hover:file:bg-clay-orange hover:file:text-clay-bg file:transition-colors file:cursor-pointer"
            />
            {files.length > 0 && (
              <p data-testid="gallery-files-selected" className="font-mono text-xs text-clay-gold mt-3 tracking-wide">
                {files.length} file{files.length === 1 ? "" : "s"} selected — {files.map((f) => f.name).join(", ").slice(0, 120)}
                {files.map((f) => f.name).join(", ").length > 120 ? "…" : ""}
              </p>
            )}
          </div>
          <div>
            <select data-testid="gallery-upload-gallery" value={gallery} onChange={(e) => setGallery(e.target.value)} className={inputCls}>
              {folders.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
              <option value="__new__">+ New folder…</option>
            </select>
            {gallery === "__new__" && (
              <input
                data-testid="gallery-new-folder-input"
                placeholder="New folder name — e.g. Season Opener 2026"
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                className={`${inputCls} mt-2`}
              />
            )}
          </div>
          <input
            data-testid="gallery-upload-caption"
            placeholder="Caption (optional) — e.g. Golden Helmet night, May 2027"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className={inputCls}
          />
          <button
            data-testid="gallery-upload-button"
            disabled={uploading}
            className="sm:col-span-2 bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </form>
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5 flex items-center gap-3">
          <FolderOpen className="w-7 h-7 text-clay-orange" /> Folders
        </h2>
        <div className="border border-clay-border divide-y divide-clay-border/60">
          {folders.map((f) => (
            <div key={f} data-testid="folder-row" className="px-4 sm:px-6 py-4">
              {renameTarget === f ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    data-testid="folder-rename-input"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-2.5 text-sm flex-1 min-w-[200px] transition-colors"
                    autoFocus
                  />
                  <button
                    data-testid="folder-rename-save"
                    onClick={() => renameFolder(f)}
                    className="bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    data-testid="folder-rename-cancel"
                    onClick={() => setRenameTarget(null)}
                    className="border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-display font-bold uppercase tracking-wide flex-1 min-w-[140px]">{f}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted border border-clay-border px-2 py-1">
                    {folderCounts[f] || 0} items
                  </span>
                  <button
                    data-testid="folder-rename-button"
                    onClick={() => { setRenameTarget(f); setRenameValue(f); setDeleteTarget(null); }}
                    className="text-clay-muted hover:text-clay-gold transition-colors p-1.5"
                    aria-label={`Rename ${f}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="folder-delete-button"
                    onClick={() => { setDeleteTarget(deleteTarget === f ? null : f); setMoveTarget(""); setRenameTarget(null); }}
                    className="text-clay-muted hover:text-red-400 transition-colors p-1.5"
                    aria-label={`Delete ${f}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              {deleteTarget === f && (
                <div data-testid="folder-delete-panel" className="border border-clay-orange/50 bg-clay-bg p-4 mt-3 space-y-3">
                  <p className="text-sm text-clay-muted">
                    "{f}" has <span className="text-clay-text font-bold">{folderCounts[f] || 0} items</span>. Move them to another
                    folder, or delete the folder and everything in it?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <select
                      data-testid="folder-move-select"
                      value={moveTarget}
                      onChange={(e) => setMoveTarget(e.target.value)}
                      className="bg-clay-card border border-clay-border focus:border-clay-orange outline-none px-3 py-2.5 text-sm transition-colors"
                    >
                      <option value="">Move items to…</option>
                      {folders.filter((x) => x !== f).map((x) => (
                        <option key={x} value={x}>{x}</option>
                      ))}
                    </select>
                    <button
                      data-testid="folder-move-button"
                      disabled={!moveTarget}
                      onClick={() => deleteFolder(f)}
                      className="border border-clay-gold text-clay-gold hover:bg-clay-gold hover:text-clay-bg disabled:opacity-40 disabled:cursor-not-allowed font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors"
                    >
                      Move Items
                    </button>
                    <button
                      data-testid="folder-delete-confirm-button"
                      onClick={() => { setMoveTarget(""); deleteFolder(f); }}
                      className="border border-red-500/60 text-red-400 hover:bg-red-500 hover:text-white font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors"
                    >
                      Delete All
                    </button>
                    <button
                      data-testid="folder-delete-cancel"
                      onClick={() => setDeleteTarget(null)}
                      className="border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight">
            Current Items <span className="text-clay-muted text-xl">({items.length})</span>
          </h2>
          <button
            data-testid="gallery-select-mode-button"
            onClick={() => { setSelectMode((s) => !s); setSelected([]); setBulkFolder(""); }}
            className={`flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${
              selectMode ? "border-clay-orange text-clay-orange" : "border-clay-border text-clay-muted hover:border-clay-orange hover:text-clay-text"
            }`}
          >
            {selectMode ? <X className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
            {selectMode ? "Cancel Select" : "Select Photos"}
          </button>
        </div>
        {selectMode && (
          <div data-testid="gallery-bulk-bar" className="flex flex-wrap items-center gap-3 border border-clay-gold/40 bg-clay-card p-4 mb-5">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-clay-gold">
              {selected.length} selected
            </span>
            <select
              data-testid="gallery-bulk-folder-select"
              value={bulkFolder}
              onChange={(e) => setBulkFolder(e.target.value)}
              className="bg-clay-bg border border-clay-border px-3 py-2 text-sm"
            >
              <option value="">Move to folder…</option>
              {folders.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <button
              data-testid="gallery-bulk-move-button"
              onClick={bulkMove}
              disabled={!bulkFolder || selected.length === 0 || bulkMoving}
              className="flex items-center gap-2 bg-clay-orange hover:bg-clay-gold text-clay-bg disabled:opacity-40 disabled:cursor-not-allowed font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors"
            >
              {bulkMoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderInput className="w-4 h-4" />}
              Move {selected.length > 0 ? selected.length : ""} Selected
            </button>
          </div>
        )}
        {items.length === 0 ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted border border-dashed border-clay-border py-12 text-center">
            Nothing uploaded yet
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid="gallery-admin-item"
                onClick={selectMode ? () => toggleSelect(item.id) : undefined}
                className={`border bg-clay-card group relative ${selectMode ? "cursor-pointer" : ""} ${
                  selectMode && selected.includes(item.id) ? "border-clay-orange ring-2 ring-clay-orange/60" : "border-clay-border"
                }`}
              >
                {selectMode && (
                  <span
                    data-testid="gallery-select-check"
                    className={`absolute top-2 right-2 z-10 ${selected.includes(item.id) ? "text-clay-orange" : "text-white/70"}`}
                  >
                    {selected.includes(item.id) ? <CheckSquare className="w-6 h-6" /> : <Square className="w-6 h-6" />}
                  </span>
                )}
                {item.content_type?.startsWith("video/") ? (
                  <div className="aspect-square bg-black grid place-items-center">
                    <Film className="w-8 h-8 text-clay-gold" />
                  </div>
                ) : (
                  <img src={`${API.replace("/api", "")}${item.url}`} alt={item.caption || ""} className="w-full aspect-square object-cover" loading="lazy" />
                )}
                {item.is_cover && (
                  <span className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.2em] bg-clay-gold text-clay-bg px-2 py-1 z-10">
                    Cover
                  </span>
                )}
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <select
                      data-testid="gallery-item-folder-select"
                      value={item.gallery}
                      onChange={(e) => moveItem(item, e.target.value)}
                      title="Move to another folder"
                      className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-gold bg-clay-card border-0 cursor-pointer hover:text-clay-text max-w-[130px] truncate p-0"
                    >
                      {[...new Set([item.gallery, ...folders])].map((x) => (
                        <option key={x} value={x}>{x}</option>
                      ))}
                    </select>
                    {!selectMode && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        data-testid="gallery-caption-edit-button"
                        onClick={() => { setCaptionEditId(item.id); setCaptionValue(item.caption || ""); }}
                        className="text-clay-muted hover:text-clay-gold transition-colors p-1"
                        aria-label="Edit caption"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {!item.content_type?.startsWith("video/") && (
                        <button
                          data-testid="gallery-cover-button"
                          onClick={() => setCover(item.id)}
                          className={`transition-colors p-1 ${item.is_cover ? "text-clay-gold" : "text-clay-muted hover:text-clay-gold"}`}
                          aria-label="Set as folder cover"
                        >
                          <Star className="w-4 h-4" fill={item.is_cover ? "currentColor" : "none"} />
                        </button>
                      )}
                      <button
                        data-testid="gallery-delete-button"
                        onClick={() => remove(item.id)}
                        className="text-clay-muted hover:text-red-400 transition-colors p-1"
                        aria-label="Delete item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    )}
                  </div>
                  {captionEditId === item.id ? (
                    <div className="flex gap-1.5">
                      <input
                        data-testid="gallery-caption-input"
                        value={captionValue}
                        onChange={(e) => setCaptionValue(e.target.value)}
                        className="bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-2.5 py-1.5 text-xs flex-1 min-w-0 transition-colors"
                        autoFocus
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), saveCaption(item.id))}
                      />
                      <button
                        data-testid="gallery-caption-save"
                        onClick={() => saveCaption(item.id)}
                        className="bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase text-xs px-3 py-1.5 transition-colors shrink-0"
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-clay-muted truncate">{item.caption || "—"}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
